import { useTranslation } from "react-i18next";
import { Box, Button, Chip, Divider, Tooltip, Typography } from "@mui/material";
import {
  TGetResourceTypesResponse,
  TLocationDoor,
  TOrder,
  TOrder$TikiRental,
  TOrdersList
} from "../api.js";
import { styled } from "@mui/material/styles";
import { Lock, LockOpen, MoreHoriz, Timer } from "@mui/icons-material";
import { useState } from "react";
import { Link } from "react-router-dom";
import { DoorReservation } from "./index.js";
import ResourceTypeDialog from "./ResourceTypeDialog.js";
import DoorActionsDialog from "./DoorActionsDialog.js";
import { RentalListModal } from "./RentalListModal.js";
import {
  filterFirstLevelResourceTypes,
  filterNormalResourceTypes,
  formatContactPersonOrOrderId,
  formatDateTimeFromReservation,
} from "./DoorCardUtils.js";
import { RentalReassignDialog } from "./RentalReassignDialog.js";

export interface DoorCardProps {
  door: TLocationDoor;
  nextReservation: DoorReservation | null;
  nextOrder: TOrder | null;
  tenantKey: string;
  resourceTypes: TGetResourceTypesResponse | null;
  selected: boolean;
  doorResourceTypes: string[];
  locationID: string;
  handleCardClick: (doorId: string | number) => void;
  handleLockClick: (doorId: string | number) => void;
  isAllowedToOpenDoor: boolean;
  rentalsHistory: TOrder$TikiRental[];
  ordersHistory: TOrdersList[] | { error: string };
}

const DoorCardBox = styled(Box)(({ theme }) => ({
  minWidth: "225px",
  width: "calc(25% - 15px)",
  minHeight: "200px",
  borderRadius: "10px",
  backgroundColor: "#b7b8b8",
  padding: "0px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  "@media (max-width: 1037px)": {
    width: "calc(33% - 14px)",
  },
  "@media (max-width: 792px)": {
    width: "calc(50% - 10px)",
    minHeight: "0",
  },
  "@media (max-width: 531px)": {
    width: "100%",
    minWidth: "100%",
    minHeight: "0",
  },
}));

const Row = styled(Box)(({ theme }) => ({
  display: "flex",
  padding: "8px",
  flexDirection: "row",
  gap: "10px",
  alignItems: "center",
}));

const Column = styled(Box)(({ theme }) => ({
  display: "flex",
  padding: "10px",
  marginBottom: "auto",
  justifyContent: "center",
  alignItems: "center",
}));

const CardCheckbox = styled("input")(({ theme }) => ({
  float: "left",
  margin: "auto 0px auto 0px",
  minHeight: "25px",
  minWidth: "25px",
  cursor: "pointer",
  accentColor: theme.palette.primary.main,
}));

const NoOverflowTypography = styled(Typography)(({ theme }) => ({
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  flex: "1",
  minWidth: "0",
}));

const NoOverFlow = styled("div")(({ theme }) => ({
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  flex: "1",
  minWidth: "0",
}));

const DialogButton = styled(Button)(({ theme }) => ({
  color: theme.palette.primary.contrastText,
}));

type OpenType = string | null;

/*
const More = styled(MoreHoriz)(({theme}) => ({
    float: 'right',
    margin: 'auto 0px auto auto',
    cursor: 'pointer',
}));
*/

export const DoorCard = (props: DoorCardProps) => {
  const { t } = useTranslation();
  const [openDialog, setOpenDialog] = useState<OpenType>(null);
  const [openDoorActionsDialog, setOpenDoorActionsDialog] = useState(false);

  const formattedContactPersonOrOrderId = formatContactPersonOrOrderId(
    props.nextOrder,
    props.tenantKey
  );
  const formattedDateTime = formatDateTimeFromReservation(
    props.nextReservation,
  );
  const firstLevelResourceTypes = filterFirstLevelResourceTypes(props);
  const normalResourceTypes = filterNormalResourceTypes(props);

  const handleDoorOptionClick = (option: string) => {
    setOpenDialog(option);
    setOpenDoorActionsDialog(false);
  };

  const handleDoorMenuOpen = () => {
    setOpenDoorActionsDialog(true);
  };

  return (
    <DoorCardBox>
      <Row
        sx={{
          borderTopLeftRadius: "10px",
          borderTopRightRadius: "10px",
          bgcolor: "#D9D9D9",
        }}
      >
        <CardCheckbox
          type={"checkbox"}
          checked={props.selected}
          onChange={() => {
            props.handleCardClick(props.door.doorID);
          }}
        />
        <Typography sx={{ m: "auto auto auto 0px" }} variant={"h5"}>
          {(props.door.doorID.length < 2 ? "0" : "") + props.door.doorID}
        </Typography>

        {formattedContactPersonOrOrderId && (
          <Tooltip sx={{ mb: "auto" }} title={formattedContactPersonOrOrderId}>
            <NoOverflowTypography variant={"h5"}>
              {props.nextReservation && (
                <Link
                  to={`/tenants/${props.tenantKey}/orders/${props.nextReservation.rental.orderID}`}
                >
                  {formattedContactPersonOrOrderId}
                </Link>
              )}
            </NoOverflowTypography>
          </Tooltip>
        )}
      </Row>

      <Divider></Divider>
      <Column>
        <Tooltip title={firstLevelResourceTypes.join(", ")}>
          <Box sx={{ display: "inline-block", width: "80%" }}>
            <NoOverflowTypography
              sx={{ fontWeight: "600", mb: "auto" }}
              variant={"h5"}
            >
              {" "}
              {firstLevelResourceTypes[0]}{" "}
            </NoOverflowTypography>
            <NoOverflowTypography
              sx={{ fontWeight: "600", mb: "auto" }}
              variant={"h5"}
            >
              {" "}
              {firstLevelResourceTypes[1]}{" "}
            </NoOverflowTypography>
          </Box>
        </Tooltip>

        <Box sx={{ display: "inline-block", width: "20%" }}>
          {props.resourceTypes && (
            <ResourceTypeDialog
              doorResourceTypes={props.doorResourceTypes}
              resourceTypes={props.resourceTypes}
              tenantKey={props.tenantKey}
              doorID={props.door.doorID}
              locationID={props.locationID}
              firstLevelResourceTypes={true}
              openStatus={openDialog === "edit_tags"}
              onClose={() => setOpenDialog(null)}
              showEditIcon={props.resourceTypes.allowCustom}
            />
          )}
        </Box>
      </Column>

      <Divider></Divider>

      <Box>
        <Row sx={{ pb: "5px", gap: "5px" }}>
          {props.nextReservation && (
            <>
              <Timer sx={{ fontSize: "20px" }}></Timer>
              <Tooltip title={formattedDateTime}>
                <NoOverflowTypography variant={"body1"}>
                  {formattedDateTime}
                </NoOverflowTypography>
              </Tooltip>
            </>
          )}
        </Row>

        <Row sx={{ pt: "0px", gap: "5px" }}>
          <Tooltip
            title={
              props.door.openState === "closed"
                ? t("doorCard.unlock")
                : t("doorCard.lock")
            }
            onClick={() =>
              props.isAllowedToOpenDoor
                ? props.handleLockClick(props.door.doorID)
                : null
            }
            style={{
              cursor: props.isAllowedToOpenDoor ? "pointer" : "not-allowed",
            }}
          >
            {props.door.openState === "closed" ? (
              <Lock sx={{ fontSize: "20px" }} />
            ) : (
              <LockOpen sx={{ fontSize: "20px" }} />
            )}
          </Tooltip>

          {props.resourceTypes && normalResourceTypes && (
              <Tooltip title={normalResourceTypes.join(", ")}>
                <NoOverFlow>
                  {normalResourceTypes?.map((normalType) => {
                    return (
                      <Chip
                        label={normalType}
                        sx={{ margin: "1px", bgcolor: "#D9D9D9" }}
                        key={normalType}
                      />
                    );
                  })}
                </NoOverFlow>
              </Tooltip>
          )}

          <DialogButton
            sx={{ padding: "0px", minWidth: "30px" }}
            onClick={handleDoorMenuOpen}
          >
            <MoreHoriz />
          </DialogButton>
          <DoorActionsDialog
            openStatus={openDoorActionsDialog}
            onClose={() => setOpenDoorActionsDialog(false)}
            onOptionClick={handleDoorOptionClick}
            showRentalList={true}
            showEditTags={true}
            showRentalReassign={props.nextOrder?.tikiOrder.source === "tiki"}
          />

          <RentalListModal
            tenantKey={props.tenantKey}
            locationID={props.locationID}
            doorID={props.door.doorID}
            openStatus={openDialog === "rental_list"}
            onClose={() => setOpenDialog(null)}
            rentalsHistory={props.rentalsHistory}
            ordersHistory={props.ordersHistory}
          />

          {props.resourceTypes && (
            <ResourceTypeDialog
              doorResourceTypes={props.doorResourceTypes}
              resourceTypes={props.resourceTypes}
              tenantKey={props.tenantKey}
              doorID={props.door.doorID}
              locationID={props.locationID}
              firstLevelResourceTypes={false}
              openStatus={openDialog === "edit_tags"}
              onClose={() => setOpenDialog(null)}
              showEditIcon={false}
            />
          )}

          {props.nextOrder?.tikiOrder.source === "tiki" &&
            props.nextReservation?.rental.rentalID && (
              <RentalReassignDialog
                rental={props.nextReservation.rental}
                tenantKey={props.tenantKey}
                locationID={props.locationID}
                doorID={props.door.doorID}
                openStatus={openDialog === "rental_reassign"}
                onClose={() => setOpenDialog(null)}
              />
            )}
        </Row>
      </Box>
    </DoorCardBox>
  );
};
